import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Button,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import QuizService from "../../../services/KangarooServices/QuizService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

const defaultQuestion = {
  questionName: "",
  options: ["", ""], // minimum 2 options
  correctAnswer: "",
};

const validationSchema = Yup.object({
  quizName: Yup.string().required("Quiz name is required"),
  quizDetail: Yup.array().of(
    Yup.object({
      questionName: Yup.string().required("Question is required"),
      options: Yup.array()
        .of(Yup.string().required("Option is required"))
        .min(2, "Minimum 2 options required")
        .max(6, "Maximum 6 options allowed"),
      correctAnswer: Yup.string().required("Correct answer is required"),
    }),
  ),
});

const QuizCreate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [detailData, setDetailData] = useState();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const removeQuestion = (index) => {
    setDeleteIndex(index);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteQuestion = () => {
    const updated = formik.values.quizDetail.filter(
      (_, i) => i !== deleteIndex,
    );

    formik.setFieldValue("quizDetail", updated);

    if (activeQuestion >= updated.length) {
      setActiveQuestion(updated.length - 1);
    }

    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };
  const fetchDetail = async () => {
    try {
      const response = await QuizService.getbySlug(slug);

      // Convert old optionA, optionB format to options[]
      const formattedQuizDetail = response.quizDetail?.map((q) => {
        const options = Object.keys(q)
          .filter((key) => key.startsWith("option"))
          .map((key) => q[key])
          .filter(Boolean);

        return {
          ...q,
          options: options.length >= 2 ? options : ["", ""],
        };
      });

      setDetailData({
        ...response,
        quizDetail: formattedQuizDetail,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (slug) fetchDetail();
  }, [slug]);

  const formik = useFormik({
    initialValues: {
      slug: detailData?.slug || "",
      quizName: detailData?.quizName || "",
      quizDetail:
        detailData?.quizDetail?.length > 0
          ? detailData.quizDetail
          : [{ ...defaultQuestion }],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        let payload = { ...values };

        // Convert options[] back to optionA, optionB format if needed
        payload.quizDetail = payload.quizDetail.map((q) => {
          const optionObj = {};
          q.options.forEach((opt, i) => {
            optionObj[`option${String.fromCharCode(65 + i)}`] = opt;
          });

          return {
            questionName: q.questionName,
            correctAnswer: q.correctAnswer,
            ...optionObj,
          };
        });

        if (slug) {
          await QuizService.update(payload);
          toast.success("Quiz updated successfully.");
        } else {
          delete payload.slug;
          await QuizService.create(payload);
          toast.success("Quiz created successfully.");
        }

        navigate("/quiz");
      } catch (error) {
        console.log("🚀 ~ QuizCreate ~ error:", error);
        const errorMessage = error?.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(", ")
          : error?.response?.data?.message || "Something went wrong";

        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const addQuestion = () => {
    formik.setFieldValue("quizDetail", [
      ...formik.values.quizDetail,
      { ...defaultQuestion },
    ]);
    setActiveQuestion(formik.values.quizDetail.length);
  };

  // const removeQuestion = (index) => {
  //   const updated = formik.values.quizDetail.filter((_, i) => i !== index);
  //   formik.setFieldValue("quizDetail", updated);
  //   if (activeQuestion >= updated.length) {
  //     setActiveQuestion(updated.length - 1);
  //   }
  // };

  const getError = (index, field) =>
    formik.touched.quizDetail?.[index]?.[field] &&
    formik.errors.quizDetail?.[index]?.[field];

  const answerOptions = (index) => {
    const q = formik.values.quizDetail[index];
    return [
      { value: "", label: "-- Select Correct Answer --" },
      ...q.options.map((opt, i) => ({
        value: opt,
        label: `Option ${i + 1}: ${opt || "Enter option"}`,
      })),
    ];
  };
  const breadcrumbItems = [{ title: "Back to List", link: "/quiz" }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Quiz Form"
          pageTitle="Quiz"
          breadcrumbItems={breadcrumbItems}
        />
        <Card>
          <CardHeader>{slug ? "Update Quiz" : "Create Quiz"}</CardHeader>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              {/* Quiz Name */}
              <FormGroup>
                <Label>Quiz Name *</Label>
                <Input
                  name="quizName"
                  value={formik.values.quizName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.quizName && formik.errors.quizName}
                />
                <FormFeedback>{formik.errors.quizName}</FormFeedback>
              </FormGroup>
              <div className="d-flex justify-content-between align-content-center align-items-center">
                <Button type="button" className="my-3" onClick={addQuestion}>
                  + Add Question
                </Button>
              </div>

              {/* Tabs */}
              <Nav tabs className="mb-3">
                {formik.values.quizDetail.map((_, i) => (
                  <NavItem key={i}>
                    <NavLink
                      onClick={() => setActiveQuestion(i)}
                      style={{
                        cursor: "pointer",
                        background:
                          activeQuestion === i ? "#6366f1" : "transparent",
                        color: activeQuestion === i ? "#fff" : "#94a3b8",
                        borderRadius: "6px",
                      }}
                    >
                      Q{i + 1}
                      {formik.values.quizDetail.length > 1 && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            removeQuestion(i);
                          }}
                          style={{ marginLeft: 10, cursor: "pointer" }}
                        >
                          ✕
                        </span>
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              {/* Active Question */}
              {formik.values.quizDetail.map((q, index) =>
                index === activeQuestion ? (
                  <div key={index}>
                    <FormGroup>
                      <div className="d-flex justify-content-between align-content-center align-items-center mb-2">
                        <Label className="w-100">
                          Question <span className="text-danger">*</span>
                        </Label>

                        <Input
                          type="select"
                          size="sm"
                          className="w-25"
                          value={q.options.length}
                          onChange={(e) => {
                            const count = Number(e.target.value);
                            let updatedOptions = [...q.options];

                            if (count > updatedOptions.length) {
                              while (updatedOptions.length < count) {
                                updatedOptions.push("");
                              }
                            } else {
                              updatedOptions = updatedOptions.slice(0, count);
                            }

                            formik.setFieldValue(
                              `quizDetail[${index}].options`,
                              updatedOptions,
                            );

                            if (!updatedOptions.includes(q.correctAnswer)) {
                              formik.setFieldValue(
                                `quizDetail[${index}].correctAnswer`,
                                "",
                              );
                            }
                          }}
                        >
                          {[2, 3, 4, 5, 6].map((num) => (
                            <option key={num} value={num}>
                              {num} Options
                            </option>
                          ))}
                        </Input>
                      </div>

                      <Input
                        type="textarea"
                        name={`quizDetail[${index}].questionName`}
                        value={q.questionName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(getError(index, "questionName"))}
                      />
                      <FormFeedback>
                        {getError(index, "questionName")}
                      </FormFeedback>
                    </FormGroup>

                    {/* Option Count Selector */}

                    {/* Dynamic Options */}
                    <Row>
                      {q.options.map((opt, optIndex) => (
                        <Col md={6} key={optIndex}>
                          <FormGroup>
                            <Label>Option {optIndex + 1}</Label>
                            <Input
                              type="textarea"
                              name={`quizDetail[${index}].options[${optIndex}]`}
                              value={opt}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                          </FormGroup>
                        </Col>
                      ))}
                    </Row>

                    {/* Correct Answer */}
                    <FormGroup>
                      <Label>Correct Answer *</Label>
                      <Input
                        type="select"
                        name={`quizDetail[${index}].correctAnswer`}
                        value={q.correctAnswer}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(getError(index, "correctAnswer"))}
                      >
                        {answerOptions(index).map((o, i) => (
                          <option key={i} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>
                        {getError(index, "correctAnswer")}
                      </FormFeedback>
                    </FormGroup>
                  </div>
                ) : null,
              )}

              <Button color="success" type="submit" disabled={isLoading}>
                {slug ? "Update Quiz" : "Submit Quiz"}
              </Button>
            </Form>
          </CardBody>
        </Card>
        <Modal
          isOpen={isDeleteModalOpen}
          toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
          centered
        >
          <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>
            Confirm Delete
          </ModalHeader>

          <ModalBody>Are you sure you want to delete this question?</ModalBody>

          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button color="danger" onClick={confirmDeleteQuestion}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </Container>
  );
};

export default QuizCreate;
